import { useEffect, useState } from "react";
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from "../../services/api/api";
import {
    Search, AlertCircle, Loader2, User,
    BookOpen, CheckCircle2, X, Clock
} from "lucide-react";
import { FastAverageColor } from "fast-average-color";
import {
    Dialog, DialogContent, DialogActions,
    Zoom, CircularProgress
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";

// --- 1. SUBCOMPONENTE: MODAL DE CONFIRMAÇÃO DE DEVOLUÇÃO ---
const ReturnConfirmDialog = ({ open, onClose, onConfirm, item, loading }: any) => {
    if (!item) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            TransitionComponent={Zoom}
            PaperProps={{
                style: {
                    borderRadius: '32px',
                    backgroundColor: '#ffffff',
                    backgroundImage: 'none',
                    maxWidth: '500px',
                    width: '100%'
                },
            }}
        >
            <div className="bg-gradient-to-r from-blue3 to-blue4 px-6 py-6 flex justify-between items-center shadow-lg">
                <div className="flex items-center gap-3">
                    <div className="bg-white/10 p-2.5 rounded-xl backdrop-blur-md border border-white/20 text-white">
                        <CheckCircle2 size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white tracking-tight leading-none">Confirmar Recebimento</h2>
                        <p className="text-blue-100 text-[9px] uppercase tracking-widest font-medium mt-1">Gestão de Acervo</p>
                    </div>
                </div>
                <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
                    <X size={20} />
                </button>
            </div>

            <DialogContent className="pt-8 pb-4">
                <div className="flex flex-col items-center text-center gap-5 font-montserrat">
                    <div className="relative">
                        <img
                            src={item.books?.img}
                            className="w-24 h-32 object-cover rounded-2xl shadow-2xl border-4 border-white transform -rotate-2"
                            alt=""
                        />
                        <div className="absolute -bottom-2 -right-2 bg-emerald-500 p-2 rounded-full border-4 border-white shadow-lg text-white">
                            <BookOpen size={16} />
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 px-4">
                        <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest leading-none">Leitor:</p>
                        <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight leading-tight">
                            {item.user?.name}
                        </h3>
                        <div className="flex items-center justify-center gap-2 text-blue-600 font-bold text-xs bg-blue-50 py-2 px-4 rounded-full mt-2">
                            <BookOpen size={14} />
                            {item.books?.title}
                        </div>
                    </div>

                    <div className="w-full grid grid-cols-2 gap-4 mt-2 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                        <div className="text-left border-r border-slate-200 pr-2">
                            <span className="text-[8px] font-black text-slate-400 uppercase block">Alugado em</span>
                            <span className="text-xs font-bold text-slate-600">{new Date(item.start).toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="text-left pl-2">
                            <span className="text-[8px] font-black text-slate-400 uppercase block">Data de Hoje</span>
                            <span className="text-xs font-bold text-emerald-600">{new Date().toLocaleDateString('pt-BR')}</span>
                        </div>
                    </div>
                </div>
            </DialogContent>

            <DialogActions className="p-6 pt-2 flex gap-3 border-t border-gray-100">
                <div className="w-full flex my-2">
                    <button onClick={onClose} className="flex-1 h-12 rounded-xl text-xs font-bold uppercase tracking-widest text-slate-400 hover:bg-slate-100 transition-all">
                        Voltar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-[2] h-12 bg-blue3 hover:bg-blue4 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 uppercase tracking-widest text-[10px] transition-all active:scale-[0.98] flex items-center justify-center"
                    >
                        {loading ? <CircularProgress size={18} color="inherit" /> : "Confirmar Devolução"}
                    </button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

// --- 2. SUBCOMPONENTE: CARD INDIVIDUAL COM COR DINÂMICA ---
const RentalCard = ({ item, checkStatus }: { item: any, checkStatus: Function }) => {
    const api = authApi();
    const queryClient = useQueryClient();
    const [gradientColor, setGradientColor] = useState('rgba(0,0,0,0.6)');
    const [openDialog, setOpenDialog] = useState(false);
    const isOverdue = checkStatus(item.end) === 'atrasado';

    useEffect(() => {
        const fac = new FastAverageColor();
        if (item.books?.img) {
            fac.getColorAsync(item.books.img)
                .then(color => {
                    setGradientColor(`linear-gradient(to bottom, ${color.rgba}, rgba(0,0,0,0.85))`);
                })
                .catch(() => setGradientColor('rgba(0,10,30,0.8)'));
        }
    }, [item.books?.img]);

    const mutation = useMutation({
        mutationFn: () => api.returnBook(item.id), // Altere no api.ts para aceitar o ID
        onSuccess: () => {
            toast.success("Livro devolvido ao acervo!");
            queryClient.invalidateQueries({ queryKey: ['rented-books'] });
            setOpenDialog(false);
        },
        onError: () => toast.error("Erro ao processar devolução.")
    });

    return (
        <>
            <div className="group relative bg-white dark:bg-[#2d3d5780] rounded-[32px] overflow-hidden border border-gray-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full font-montserrat">
                <div className="relative h-48 flex justify-center items-center overflow-hidden bg-slate-100  p-4">
                    <img
                        src={item.books?.img}
                        className="max-h-40 object-contain z-10 transition-transform duration-500 group-hover:scale-110 drop-shadow-2xl"
                        alt={item.books?.title}
                    />
                    <div className="absolute inset-0" style={{ background: gradientColor }} />
                    <div className="absolute top-4 left-4 z-20">
                        <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg ${isOverdue ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'}`}>
                            {isOverdue ? 'Atrasado' : 'Reservado'}
                        </span>
                    </div>
                </div>

                <div className="p-5 flex flex-col flex-1">
                    <div className="mb-4">
                        <h4 className="font-black text-slate-800 dark:text-white text-xs uppercase truncate leading-tight">
                            {item.books?.title}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-1 text-blue5 font-medium">
                            <User size={12} />
                            <span className="text-[10px] truncate uppercase tracking-widest font-bold opacity-70 dark:opacity-100">
                                {item.user?.name}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 py-3 border-t border-gray-100 dark:border-white/5 mt-auto">
                        <div className="flex flex-col">
                            <span className="text-[8px] uppercase font-bold text-gray-400 tracking-tighter">Retirada</span>
                            <span className="text-[10px] font-bold dark:text-gray-300">
                                {new Date(item.start).toLocaleDateString('pt-BR')}
                            </span>
                        </div>
                        <div className="flex flex-col text-right">
                            <span className="text-[8px] uppercase font-bold text-gray-400 tracking-tighter">Prazo</span>
                            <span className={`text-[10px] font-bold ${isOverdue ? 'text-red-500' : 'text-blue-500'}`}>
                                {new Date(item.end).toLocaleDateString('pt-BR')}
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={() => setOpenDialog(true)}
                        className={`mt-3 w-full py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 border ${isOverdue ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-600 hover:text-white' : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-600 hover:text-white dark:bg-white/5 dark:text-gray-300 dark:border-white/10 dark:hover:bg-blue-600 dark:hover:text-white'
                            }`}
                    >
                        Devolver
                    </button>
                </div>
            </div>

            <ReturnConfirmDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                onConfirm={() => mutation.mutate()}
                item={item}
                loading={mutation.isPending}
            />


        </>
    );
};

// --- 3. COMPONENTE PRINCIPAL: RENTEDBOOKS ---
export default function RentedBooks() {
    const api = authApi();
    const { ref, inView } = useInView();
    const [search, setSearch] = useState("");

    const getData = async ({ pageParam = 1 }) => {
        return await api.listRentals(pageParam, search);
    };

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, status } = useInfiniteQuery({
        queryKey: ['rented-books', search],
        queryFn: getData,
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            if (lastPage.current_page < lastPage.last_page) return lastPage.current_page + 1;
            return undefined;
        }
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage]);

    const checkStatus = (dueDate: string) => {
        return new Date(dueDate) < new Date() ? 'atrasado' : 'no_prazo';
    };

    return (
        <div className="w-full animate-in fade-in duration-500">
            {/* <div className="relative mb-10 group max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
                <input
                    type="text"
                    placeholder="Pesquisar leitor ou livro..."
                    className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-white dark:bg-white/10 border border-gray-100 dark:border-white/5 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/50 transition-all text-sm shadow-sm font-montserrat"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div> */}

            {status === 'pending' ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-4 lg:gap-6">
                    {[1, 2, 3, 4, 5, 6, 7].map(i => (
                        <div key={i} className="h-80 w-full bg-gray-200 dark:bg-white/5 animate-pulse rounded-[32px]" />
                    ))}
                </div>
            ) : data?.pages[0].data.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-gray-400 bg-gray-50 dark:bg-white/5 rounded-[40px] border-2 border-dashed border-gray-200 dark:border-white/5">
                    <AlertCircle size={56} strokeWidth={1} className="mb-4 opacity-10" />
                    <p className="text-[10px] font-black uppercase tracking-[0.3em]">Nenhum aluguel encontrado</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 lg: gap-4 lg:gap-6">
                    {data.pages.map((page) =>
                        page.data.map((item: any) => (
                            <RentalCard key={item.id} item={item} checkStatus={checkStatus} />
                        ))
                    )}
                </div>
            )}

            <div ref={ref} className="h-24 flex justify-center items-center mt-8">
                {isFetchingNextPage && <Loader2 className="animate-spin text-blue-600" size={36} />}
            </div>
            <ToastContainer />

        </div>
    );
}